import React, { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Clock, ArrowRight, ChevronLeft } from 'lucide-react';
import { fieldNotes, FieldNote } from '../data/fieldNotes';
import { useSEO } from '../hooks/useSEO';
import SiteNav from './SiteNav';
import SiteFooter from './SiteFooter';

const CATEGORIES = ['All', 'Destinations', 'Culture', 'Cuisine', 'Architecture', 'Hidden Gems', 'Travel Intelligence'] as const;

const Journeys: React.FC = () => {
  const navigate = useNavigate();
  useSEO({
    title: 'Field Notes — Travel Stories & Destination Guides | Aura Traveler',
    description: 'Explore curated travel stories, destination guides, and cultural deep-dives from the world\'s most extraordinary places.',
    canonical: 'https://www.auratraveler.com/field-notes',
  });
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = useMemo(() => {
    let posts = fieldNotes;
    if (activeCategory !== 'All') {
      posts = posts.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.author.toLowerCase().includes(q) ||
          p.content.toLowerCase().includes(q) ||
          p.slug.toLowerCase().includes(q)
      );
    }
    return posts;
  }, [activeCategory, searchQuery]);

  const featuredPost = filteredPosts.find((p) => p.featured) || filteredPosts[0];
  const gridPosts = filteredPosts.filter((p) => p !== featuredPost);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#1A1A1A]">
      <SiteNav />
      {/* Hero Banner */}
      <section className="relative bg-[#1A1A1A] text-white py-32 md:py-40 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-[#1A1A1A]" />
        <div className="relative max-w-5xl mx-auto text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60 hover:text-[#C5A059] transition-colors duration-300 mb-12"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Back to Home
          </button>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-display text-5xl md:text-7xl tracking-tight mb-6"
          >
            Field Notes
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-serif italic text-lg md:text-xl text-white/60 max-w-xl mx-auto"
          >
            Stories, secrets, and the art of extraordinary travel
          </motion.p>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="max-w-6xl mx-auto px-6 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl shadow-black/5 p-6 md:p-8">
          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40" />
            <input
              type="text"
              placeholder="Search stories..."
              aria-label="Search field notes by title, destination, or topic"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-[#FAF8F5] border border-black/5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40 transition-all placeholder:text-[#1A1A1A]/30"
            />
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                  activeCategory === cat
                    ? 'bg-[#1A1A1A] text-white'
                    : 'bg-[#FAF8F5] text-[#1A1A1A]/60 hover:text-[#C5A059]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        {filteredPosts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-lg text-[#1A1A1A]/40 font-serif italic">No stories found matching your search.</p>
          </div>
        )}

        {/* Featured Post */}
        {featuredPost && (
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            onClick={() => navigate(`/field-notes/${featuredPost.slug}`)}
            className="group cursor-pointer mb-20"
          >
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] sm:aspect-[21/9] md:aspect-[21/8]">
              <img
                src={featuredPost.heroImage}
                alt={featuredPost.title}
                loading="eager"
                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.classList.add('bg-gradient-to-br', 'from-[#2a2a2a]', 'to-[#1a1a1a]'); }}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <span className="inline-block px-3 py-1 bg-[#C5A059] text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-full mb-4">
                  {featuredPost.category}
                </span>
                <h2 className="font-display text-2xl md:text-4xl text-white mb-3 tracking-tight">
                  {featuredPost.title}
                </h2>
                <p className="text-white/70 text-sm md:text-base max-w-2xl mb-4 font-serif italic">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center gap-4 text-white/50 text-xs">
                  <span>{featuredPost.author}</span>
                  <span className="w-1 h-1 rounded-full bg-white/30" />
                  <span>{formatDate(featuredPost.date)}</span>
                  <span className="w-1 h-1 rounded-full bg-white/30" />
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {featuredPost.readTime}
                  </span>
                </div>
              </div>
            </div>
          </motion.article>
        )}

        {/* Post Grid */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.1 } },
          }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12"
        >
          {gridPosts.map((post) => (
            <PostCard key={post.slug} post={post} onClick={() => navigate(`/field-notes/${post.slug}`)} formatDate={formatDate} />
          ))}
        </motion.div>

        {/* Cross-link to Journal */}
        <div className="mt-20 pt-12 border-t border-black/10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-8 md:p-10 bg-white rounded-2xl shadow-lg shadow-black/5">
            <div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#C5A059] mb-2 block">The Journal</span>
              <h3 className="font-display text-lg md:text-xl tracking-tight mb-1">Industry Intelligence</h3>
              <p className="text-sm text-[#1A1A1A]/50 font-serif italic">
                Insights on luxury travel design, AI in agencies, and the business of extraordinary experiences.
              </p>
            </div>
            <Link
              to="/journal"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] text-white text-[10px] font-semibold uppercase tracking-[0.15em] rounded-full hover:opacity-80 transition-opacity flex-shrink-0"
            >
              Read the Journal <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Product CTA */}
      <section className="py-20 px-4 sm:px-6 md:px-12 bg-[#1A1A1A] border-t border-black/10">
        <div className="max-w-2xl mx-auto text-center">
          <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-4 block">Start Your Journey</span>
          <h2 className="font-display text-2xl md:text-3xl text-white tracking-tight mb-4">
            Ready to plan something extraordinary?
          </h2>
          <p className="font-serif text-base text-white/50 italic mb-8 max-w-lg mx-auto leading-relaxed">
            AI-curated, magazine-quality travel itineraries — generated in under 90 seconds.
          </p>
          <Link
            to="/"
            className="inline-block px-10 py-4 bg-[#C5A059] text-black font-sans text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-white transition-all duration-500"
          >
            Start Planning
          </Link>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

interface PostCardProps {
  post: FieldNote;
  onClick: () => void;
  formatDate: (d: string) => string;
}

const PostCard: React.FC<PostCardProps> = ({ post, onClick, formatDate }) => {
  return (
    <motion.article
      variants={{
        hidden: { opacity: 0, y: 24 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
      }}
      onClick={onClick}
      className="group cursor-pointer"
    >
      <div className="relative rounded-xl overflow-hidden aspect-[4/3] mb-6">
        <img
          src={post.heroImage}
          alt={post.title}
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.classList.add('bg-gradient-to-br', 'from-[#2a2a2a]', 'to-[#1a1a1a]'); }}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <span className="absolute top-4 left-4 px-3 py-1 bg-[#1A1A1A]/80 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-full">
          {post.category}
        </span>
      </div>
      <h3 className="font-display text-lg md:text-xl tracking-tight mb-2 group-hover:text-[#C5A059] transition-colors duration-300">
        {post.title}
      </h3>
      <p className="text-sm text-[#1A1A1A]/60 mb-5 line-clamp-2 font-serif italic">
        {post.excerpt}
      </p>
      <div className="flex items-center justify-between text-[11px] text-[#1A1A1A]/40">
        <div className="flex items-center gap-3">
          <span className="font-medium">{post.author}</span>
          <span className="w-1 h-1 rounded-full bg-current" />
          <span>{formatDate(post.date)}</span>
        </div>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {post.readTime}
        </span>
      </div>
      <div className="mt-4 flex items-center gap-1.5 text-[#C5A059] text-[11px] font-semibold uppercase tracking-[0.15em] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
        Read Story <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </motion.article>
  );
};

export default Journeys;
